import { UserProperties } from '@futrli-sdk/users';
import { AuthenticatorUser, Logger, QUERY_PARAMS } from '@futrli/shared-services';
import { getUserProperties, findProperty, updateUserProperty } from '@futrli/users';
import { formatISO } from 'date-fns';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import queryString from 'query-string';
import { capitalize } from 'lodash';

import { DispatchAny, StoreState } from '../../core/types';
import {
  setCurrentAuthenticatedUserPending,
  getCurrentAuthenticatedUser,
  setCurrentAuthenticatedUserFulfilled,
  setCurrentAuthenticatedUserRejected
} from '../../auth/redux-utils/actions';
import { getTracker } from '../../core/injected';
import { FederatedSignInStorage } from '../types';
import { FEDERATED_SIGN_IN_SRC } from '../constants';

/**
 * Gets the authenticated user once the user has been redirected from the HostedUI (Xero)
 * and stores the data in the redux store to get the user logged in and redirected to the
 * onboarding.
 *
 * @param {() => Promise<void>} onRedirect - on success redirect callback
 */
export const getUserLoggedInAfterSSU = (
  onRedirect: () => Promise<void>,
  storage: FederatedSignInStorage
): DispatchAny => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    try {
      dispatch(setCurrentAuthenticatedUserPending());

      const user = await dispatch(getCurrentAuthenticatedUser());

      if (user?.attributes && user.attributes.sub) {
        trackFederatedSignIn(user, storage);
        await updateUserProperties(storage);
      }

      await onRedirect();
      dispatch(setCurrentAuthenticatedUserFulfilled(user));
    } catch (error) {
      dispatch(setCurrentAuthenticatedUserRejected());
      Logger.error(String(error));
    }
  };
};

/**
 * Gets the authenticated user once the user has been redirected from the HostedUI (Xero)
 * and stores the data in the redux store to get the user logged in and redirected to the
 * root path.
 *
 * @param {() => Promise<void> | undefined} onRedirect - on success redirect callback
 */
export const getUserLoggedInAfterSSO = (
  onRedirect: () => Promise<void>,
  storage: FederatedSignInStorage
): DispatchAny => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    try {
      dispatch(setCurrentAuthenticatedUserPending());
      const user = await dispatch(getCurrentAuthenticatedUser());

      trackFederatedSignIn(user, storage);
      await updateUserProperties(storage);

      await onRedirect();

      dispatch(setCurrentAuthenticatedUserFulfilled(user));
    } catch (error) {
      dispatch(setCurrentAuthenticatedUserRejected());
      Logger.error(String(error));
    }
  };
};

/**
 * Tracks federated sign in data once the user is already signed in, through SSO, in Cognito.
 * @param {AuthenticatorUser} user
 * @param {FederatedSignInStorage} storage
 */
export const trackFederatedSignIn = (user: AuthenticatorUser, storage: FederatedSignInStorage) => {
  const tracker = getTracker();
  const originQueryParams = queryString.parse(storage.originLocationSearch);
  tracker.identify(user.attributes.sub, {
    firstName: user.attributes.given_name,
    lastName: user.attributes.family_name,
    email: user.attributes.email
  });
  tracker.track('User signed in through SSO', {
    'SSO provider': capitalize(storage.federatedIdentityProvider),
    'SSO trigger source': capitalize((originQueryParams[QUERY_PARAMS.SRC] as string) || 'Futrli App')
  });
};

/**
 * Updated user properties in WSC.
 * @param {FederatedSignInStorage} storage
 */
export const updateUserProperties = async (storage: FederatedSignInStorage) => {
  const originQueryParams = queryString.parse(storage.originLocationSearch);
  const externalSrc = originQueryParams[QUERY_PARAMS.SRC];

  try {
    const userProperties = await getUserProperties();
    const firstAccessFromSSOExternalService =
      userProperties && findProperty(userProperties, UserProperties.first_access_from_sso_xero_marketplace)?.value;

    // We need to store in WSC the first time the user access to the system through SSO
    // and from an external service like Xero Marketplace, in this way we'll be able to
    // determine if the user has been created at this point or was already created.
    if (!firstAccessFromSSOExternalService && externalSrc === FEDERATED_SIGN_IN_SRC.XERO_MARKETPLACE) {
      await updateUserProperty({
        property: UserProperties.first_access_from_sso_xero_marketplace,
        value: formatISO(Date.now())
      });
      return;
    }
  } catch (error) {
    Logger.error(String(error));
  }
};
